import { supabase } from './supabase';

declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        iosHandler?: {
          postMessage: (message: string) => void;
        };
      };
    };
  }
}

export interface IOSMessage {
  action: string;
  payload?: Record<string, unknown>;
}

export function isRunningInIOSApp(): boolean {
  return !!(window.webkit?.messageHandlers?.iosHandler);
}

export function sendToIOS(message: IOSMessage): void {
  if (isRunningInIOSApp()) {
    window.webkit?.messageHandlers?.iosHandler?.postMessage(JSON.stringify(message));
  }
}

export function requestNotificationPermission(): void {
  sendToIOS({ action: 'requestNotificationPermission' });
}

export function setAudioSessionPlayback(): void {
  sendToIOS({ action: 'setAudioSessionPlayback' });
}

export function setAudioSessionRecord(): void {
  sendToIOS({ action: 'setAudioSessionRecord' });
}

export function registerDeviceToken(token: string): void {
  sendToIOS({ action: 'registerDeviceToken', payload: { token } });
}

export async function saveDeviceToken(userId: string, deviceToken: string, deviceName?: string): Promise<void> {
  const { error } = await supabase
    .from('device_tokens')
    .upsert(
      {
        user_id: userId,
        device_token: deviceToken,
        device_type: 'ios',
        device_name: deviceName || 'iPhone',
        is_active: true,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: 'user_id,device_token',
      }
    );

  if (error) {
    console.error('Error saving device token:', error);
  }
}

export async function removeDeviceToken(userId: string, deviceToken: string): Promise<void> {
  const { error } = await supabase
    .from('device_tokens')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('device_token', deviceToken);

  if (error) {
    console.error('Error removing device token:', error);
  }
}

async function handleIOSMessageInternal(userId: string, messageJson: string): Promise<void> {
  try {
    const message = JSON.parse(messageJson) as IOSMessage;

    switch (message.action) {
      case 'deviceTokenReceived':
        if (message.payload?.token) {
          await saveDeviceToken(userId, message.payload.token as string, message.payload.deviceName as string);
        }
        break;
      case 'notificationPermissionGranted':
        console.log('Notification permission granted');
        break;
      case 'notificationPermissionDenied':
        console.log('Notification permission denied');
        break;
      default:
        console.log('Unknown iOS message:', message);
    }
  } catch (e) {
    console.error('Error parsing iOS message:', e);
  }
}

export function setupIOSBridge(userId: string): void {
  const handler = (messageJson: string) => handleIOSMessageInternal(userId, messageJson);

  (window as Window & { handleIOSMessage?: (messageJson: string) => void }).handleIOSMessage = handler;

  const eventHandler = (event: Event) => {
    const customEvent = event as CustomEvent;
    if (customEvent.detail) {
      handler(customEvent.detail);
    }
  };

  window.addEventListener('iosMessage', eventHandler);

  sendToIOS({ action: 'requestDeviceToken' });
}
